import { useState, useCallback, useEffect } from "react";
import { Link } from "react-router-dom";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight, MapPin, Building2, Hash, ArrowUpRight } from "lucide-react";
import { NA_COVERAGE, COUNTRIES } from "@/data/coverage";

/**
 * Horizontal slider showing every covered state/province.
 * - Country tabs filter the slider (USA / Canada / Australia)
 * - Embla carousel handles drag + arrow navigation
 * - Each card shows: state code, name, hub, cities, sample zips, county count
 */
export default function StatesSlider({ heading = "Coverage across North America", subheading }) {
  const [country, setCountry] = useState("USA");
  const list = NA_COVERAGE.filter((s) => s.country === country);

  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    loop: false,
    containScroll: "trimSnaps",
    skipSnaps: false,
  });

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);

  // Re-init when country tab changes so first slide is in view
  useEffect(() => { if (emblaApi) emblaApi.reInit(); }, [country, emblaApi]);

  return (
    <section className="border-b border-slate-200" data-testid="states-slider-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16">
        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-end mb-8">
          <div className="lg:col-span-7">
            <div className="mono text-[10px] tracking-[0.22em] uppercase text-topo">North American Coverage</div>
            <h2 className="display mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tighter text-slate-900">
              {heading}
            </h2>
            {subheading && (
              <p className="mt-3 text-slate-600 leading-relaxed max-w-2xl">{subheading}</p>
            )}
          </div>

          {/* Country tabs + arrows */}
          <div className="lg:col-span-5 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-end">
            <div className="inline-flex border border-slate-300 bg-white rounded-sm overflow-hidden" data-testid="country-tabs">
              {COUNTRIES.map((c) => (
                <button
                  key={c}
                  data-testid={`country-tab-${c.toLowerCase()}`}
                  onClick={() => setCountry(c)}
                  className={`px-4 py-2 text-sm font-semibold mono tracking-[0.12em] uppercase transition-colors ${
                    country === c
                      ? "bg-orange-survey text-white"
                      : "bg-white text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <button
                onClick={scrollPrev}
                data-testid="slider-prev"
                aria-label="Previous state"
                className="inline-flex h-10 w-10 items-center justify-center border border-slate-300 hover:border-cyanotype hover:bg-cyanotype hover:text-cream rounded-sm transition-colors"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                onClick={scrollNext}
                data-testid="slider-next"
                aria-label="Next state"
                className="inline-flex h-10 w-10 items-center justify-center border border-slate-300 hover:border-cyanotype hover:bg-cyanotype hover:text-cream rounded-sm transition-colors"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Embla viewport */}
        <div className="overflow-hidden -mx-2" ref={emblaRef} data-testid="states-slider">
          <div className="flex">
            {list.map((s, i) => (
              <article
                key={s.code}
                data-testid={`state-card-${s.code}`}
                className="flex-[0_0_88%] sm:flex-[0_0_55%] lg:flex-[0_0_36%] px-2"
              >
                <div className="border border-slate-200 bg-white p-6 lg:p-7 h-full flex flex-col tri-card">
                  {/* Header row */}
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="display text-4xl font-extrabold tracking-tighter text-topo leading-none">
                        {s.code}
                      </div>
                      <div className="leading-tight">
                        <div className="display text-lg font-extrabold tracking-tight text-slate-900">{s.name}</div>
                        <div className="mono text-[10px] tracking-[0.18em] uppercase text-slate-500 mt-0.5">
                          {s.flag} {s.country} · {s.hub}
                        </div>
                      </div>
                    </div>
                    <div className="mono text-[10px] tracking-[0.18em] uppercase text-slate-400">
                      {String(i + 1).padStart(2, "0")} / {String(list.length).padStart(2, "0")}
                    </div>
                  </div>

                  {/* Note */}
                  <p className="mt-5 text-sm text-slate-600 leading-relaxed">{s.note}</p>

                  {/* Cities */}
                  <div className="mt-5 grid grid-cols-2 gap-y-1.5 gap-x-3">
                    <div className="col-span-2 mono text-[10px] tracking-[0.22em] uppercase text-emerald-700 flex items-center gap-1.5">
                      <Building2 className="h-3 w-3" /> Key Cities
                    </div>
                    {s.cities.map((c) => (
                      <div key={c} className="flex items-center gap-1.5 text-sm text-slate-700">
                        <MapPin className="h-3 w-3 text-slate-400 shrink-0" />
                        <span className="truncate">{c}</span>
                      </div>
                    ))}
                  </div>

                  {/* ZIPs */}
                  <div className="mt-5">
                    <div className="mono text-[10px] tracking-[0.22em] uppercase text-emerald-700 flex items-center gap-1.5">
                      <Hash className="h-3 w-3" /> Sample {s.country === "Canada" ? "Postal Codes" : "ZIP Codes"}
                    </div>
                    <ul className="mt-2 mono text-xs text-slate-700 grid grid-cols-3 gap-1">
                      {s.zips.map((z) => (
                        <li key={z} className="border border-dashed border-slate-200 px-2 py-1 text-center">{z}</li>
                      ))}
                    </ul>
                  </div>

                  {/* Footer stat + CTA */}
                  <div className="mt-auto pt-5 flex items-center justify-between border-t border-slate-100 gap-3">
                    <div>
                      <div className="display text-2xl font-extrabold tracking-tighter text-slate-900">{s.counties}</div>
                      <div className="mono text-[10px] tracking-[0.18em] uppercase text-slate-500">
                        {s.country === "Canada" ? "Census divisions" : s.country === "Australia" ? "LGAs (~)" : "Counties"}
                      </div>
                    </div>
                    <Link
                      to={`/contact?country=${encodeURIComponent(s.country)}&state=${encodeURIComponent(s.name)}&code=${s.code}`}
                      data-testid={`state-specialist-btn-${s.code}`}
                      className="inline-flex items-center gap-1.5 rounded-sm bg-orange-survey hover:bg-orange-survey-dark text-white text-xs font-semibold px-3 py-2 mono tracking-[0.08em] uppercase transition-colors"
                    >
                      Talk to a {s.code} specialist <ArrowUpRight className="h-3.5 w-3.5" />
                    </Link>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>

        {/* Coverage caption */}
        <div className="mt-6 mono text-[10px] tracking-[0.22em] uppercase text-slate-500 text-center">
          {list.length} {country === "USA" ? "U.S. states" : country === "Canada" ? "Canadian provinces" : "Australian states"} actively covered
          · Drag, swipe, or use ◀ ▶ to navigate
        </div>
      </div>
    </section>
  );
}
